import React, { createContext, useContext, useState, useEffect } from "react";
import { translations, RTL_LANGUAGES, LANGUAGES } from "./i18n";
import { extraTranslations } from "./i18n-extra";
import { runtimeConfig } from "./runtimeConfig";

// Merge extra translations into main translations
const mergedTranslations = Object.fromEntries(
  Object.keys(translations).map(lang => [
    lang,
    { ...translations[lang], ...(extraTranslations[lang] || {}) }
  ])
);

const LanguageContext = createContext();
const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map((language) => language.code);

function normalizeLanguage(value) {
  const language = String(value || "").trim().toLowerCase();
  return SUPPORTED_LANGUAGE_CODES.includes(language) ? language : "";
}

function getPublicLanguagePath(pathname) {
  const path = String(pathname || "/").replace(/\/+$/, "") || "/";
  if (path === "/" || path === "/welcome" || path === "/faq" || path === "/privacy" || path === "/terms" || path === "/rules") {
    return path;
  }
  if (path === "/blog" || path.startsWith("/blog/")) return path;
  return "";
}

function getInitialLanguage() {
  if (typeof window === "undefined") return "en";
  const queryLanguage = normalizeLanguage(new URLSearchParams(window.location.search).get("lang"));
  if (queryLanguage) return queryLanguage;
  return normalizeLanguage(localStorage.getItem("souvenir_lang")) || "en";
}

function updateLanguageUrl(language) {
  if (typeof window === "undefined") return;
  if (!getPublicLanguagePath(window.location.pathname)) return;

  const url = new URL(window.location.href);
  if (language === "en") {
    url.searchParams.delete("lang");
  } else {
    url.searchParams.set("lang", language);
  }
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function applyRuntimeBrand(value) {
  if (typeof value !== "string") return value;

  return value
    .split("Souvenir")
    .join(runtimeConfig.appName)
    .split("سوفنير")
    .join(runtimeConfig.appName)
    .split("hello@souvenir.app")
    .join(runtimeConfig.supportEmail);
}

export function LanguageProvider({ children }) {
  const [lang, setRawLang] = useState(getInitialLanguage);

  const setLang = React.useCallback((nextLanguage) => {
    const language = normalizeLanguage(nextLanguage) || "en";
    setRawLang(language);
    updateLanguageUrl(language);
  }, []);

  const t = (key) => applyRuntimeBrand(
    mergedTranslations[lang]?.[key] ?? mergedTranslations.en?.[key] ?? key
  );
  const isRTL = RTL_LANGUAGES.includes(lang);

  useEffect(() => {
    localStorage.setItem("souvenir_lang", lang);
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", lang);
    updateLanguageUrl(lang);
  }, [lang, isRTL]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
